import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { LineChart } from 'react-native-chart-kit';
import { Colors } from '@/constants/colors';
import { SentimentDataPoint } from '@/types/historical-sentiment.types';

export type TimePeriod = '1W' | '1M' | '3M' | '1Y';

interface SentimentHistoryChartProps {
  dataPoints: SentimentDataPoint[];
  averageEdge: number | null;
  onPeriodChange?: (period: TimePeriod) => void;
  selectedPeriod?: TimePeriod;
}

/**
 * Sentiment History Chart
 * Shows interactive line chart of Group vs Market sentiment
 * Green line = Your Group, Gray line = Market
 * Supports 1W, 1M, 3M, 1Y time periods
 */
export const SentimentHistoryChart: React.FC<SentimentHistoryChartProps> = ({
  dataPoints,
  averageEdge,
  onPeriodChange,
  selectedPeriod = '1W',
}) => {
  const [activePeriod, setActivePeriod] = useState<TimePeriod>(selectedPeriod);
  const { width: windowWidth } = useWindowDimensions();

  // Sync local state with prop changes
  useEffect(() => {
    setActivePeriod(selectedPeriod);
  }, [selectedPeriod]);

  const handlePeriodChange = (period: TimePeriod) => {
    setActivePeriod(period);
    onPeriodChange?.(period);
  };

  const periods: TimePeriod[] = ['1W', '1M', '3M', '1Y'];
  const hasData = dataPoints.length > 0;

  // Prepare chart data only if we have data
  const labels = hasData
    ? dataPoints.map((dp, index) => {
        const date = new Date(dp.date);

        if (activePeriod === '1W') {
          // For 1 week: Show all days with day names
          return date.toLocaleDateString('en-US', { weekday: 'short' }).substring(0, 3);
        } else if (activePeriod === '1M') {
          // For 1 month: Show week numbers (W1, W2, W3, W4) or dates
          const dayOfMonth = date.getDate();
          if (dayOfMonth === 1 || index === 0) {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }
          if (index % 7 === 0) {
            // Show every 7 days
            return date.getDate().toString();
          }
          return '';
        } else if (activePeriod === '3M') {
          // For 3 months: Show month names
          if (index === 0 || date.getDate() === 1) {
            return date.toLocaleDateString('en-US', { month: 'short' });
          }
          if (index % 14 === 0) {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }
          return '';
        } else {
          // For 1 year: Show quarterly markers with year
          if (index === 0 || index === dataPoints.length - 1) {
            return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          }
          if (index % 90 === 0) {
            // Show every ~3 months
            return date.toLocaleDateString('en-US', { month: 'short' });
          }
          return '';
        }
      })
    : [];

  const groupSentiments = hasData ? dataPoints.map(dp => dp.groupSentiment ?? 50) : [];
  const marketSentiments = hasData ? dataPoints.map(dp => dp.marketSentiment) : [];

  const chartData = hasData
    ? {
        labels,
        datasets: [
          {
            data: groupSentiments,
            color: (opacity = 1) => `rgba(16, 185, 129, ${opacity})`, // Green for group
            strokeWidth: 3,
          },
          {
            data: marketSentiments,
            color: (opacity = 1) => `rgba(156, 163, 175, ${opacity})`, // Gray for market
            strokeWidth: 2,
          },
        ],
        legend: ['Your Group', 'Market'],
      }
    : null;

  const chartConfig = {
    backgroundColor: Colors.background.secondary,
    backgroundGradientFrom: Colors.background.secondary,
    backgroundGradientTo: Colors.background.secondary,
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(255, 255, 255, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(156, 163, 175, ${opacity})`,
    style: {
      borderRadius: 8,
    },
    propsForDots: {
      r: '4',
      strokeWidth: '2',
    },
    propsForBackgroundLines: {
      strokeDasharray: '', // solid background lines
      stroke: Colors.border.subtle,
      strokeWidth: 1,
    },
  };

  // Responsive chart width - adapts to screen size and orientation
  const chartWidth = windowWidth - 64; // Account for padding (32px each side) + container padding (16px each)

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>SENTIMENT HISTORY</Text>
        <Text style={styles.subtitle}>Last {dataPoints.length} days</Text>
      </View>

      {/* Time Period Selector - All periods unlocked */}
      <View style={styles.periodSelector}>
        {periods.map(period => {
          const isActive = activePeriod === period;

          return (
            <TouchableOpacity
              key={period}
              style={[styles.periodTab, isActive && styles.periodTabActive]}
              onPress={() => handlePeriodChange(period)}
              activeOpacity={0.7}
            >
              <Text style={[styles.periodTabText, isActive && styles.periodTabTextActive]}>
                {period}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Show legend and chart only if we have data */}
      {hasData ? (
        <>
          {/* Legend */}
          <View style={styles.legend}>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, styles.groupDot]} />
              <Text style={styles.legendText}>Your Group</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, styles.marketDot]} />
              <Text style={styles.legendText}>Market</Text>
            </View>
            {averageEdge !== null && (
              <View style={styles.edgeBadge}>
                <Text style={styles.edgeText}>
                  {averageEdge > 0 ? '+' : ''}
                  {averageEdge}% avg edge
                </Text>
              </View>
            )}
          </View>

          {/* Chart */}
          <LineChart
            data={chartData!}
            width={chartWidth}
            height={220}
            chartConfig={chartConfig}
            bezier
            style={styles.chart}
            withInnerLines={true}
            withOuterLines={false}
            withVerticalLines={false}
            withHorizontalLines={true}
            withVerticalLabels={true}
            withHorizontalLabels={true}
            fromZero={false}
            yAxisSuffix="%"
            segments={4}
          />

          {/* Insight */}
          {averageEdge !== null && (
            <View style={styles.insight}>
              <Text style={styles.insightText}>
                {averageEdge > 15
                  ? '🎯 Your group is ahead of market sentiment'
                  : averageEdge > 5
                    ? '⚡ Your group is slightly ahead'
                    : averageEdge > -5
                      ? '— Your group is in sync with market'
                      : averageEdge > -15
                        ? '⚠️ Your group is contrarian'
                        : '🔴 Your group is heavily contrarian'}
              </Text>
            </View>
          )}
        </>
      ) : (
        /* Empty state when no data for selected period */
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>No data for {activePeriod} period</Text>
          <Text style={styles.emptyStateSubtext}>
            Start chatting about stocks to build your group's sentiment history
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  title: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.text.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: 11,
    color: Colors.text.tertiary,
  },
  periodSelector: {
    flexDirection: 'row',
    backgroundColor: Colors.background.primary,
    borderRadius: 8,
    padding: 4,
    marginBottom: 12,
    gap: 4,
  },
  periodTab: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  periodTabActive: {
    backgroundColor: Colors.accent.primary,
  },
  periodTabText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.tertiary,
  },
  periodTabTextActive: {
    color: Colors.text.primary,
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  groupDot: {
    backgroundColor: Colors.market.profit, // Green
  },
  marketDot: {
    backgroundColor: Colors.text.tertiary, // Gray
  },
  legendText: {
    fontSize: 11,
    color: Colors.text.secondary,
  },
  edgeBadge: {
    marginLeft: 'auto',
    backgroundColor: `${Colors.ai.primary}20`,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
  },
  edgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: Colors.ai.primary,
  },
  chart: {
    marginVertical: 8,
    borderRadius: 8,
  },
  insight: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  insightText: {
    fontSize: 12,
    color: Colors.text.secondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  emptyContainer: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 32,
    alignItems: 'center',
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 4,
  },
  emptySubtext: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
  emptyState: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 6,
  },
  emptyStateSubtext: {
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'center',
  },
  emptyStateHint: {
    fontSize: 11,
    color: Colors.ai.primary,
    textAlign: 'center',
    marginTop: 12,
    fontWeight: '600',
  },
});
